import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, ArrowUturnLeftIcon, MagnifyingGlassIcon, } from '@heroicons/react/24/outline';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { Spinner } from '@/components/Spinner';
import { useFoods, useCompanyWithPrices, useSetCompanyFoodPrice, useDeleteCompanyFoodPrice, } from '@/hooks/useApi';
import { toast } from '@/components/ui/Toast';
import { formatToman, toPersianDigits } from '@/lib/jalali';
export function CompanyPricesModal({ open, onClose, company }) {
    const { t } = useTranslation();
    const foodsQ = useFoods();
    const companyQ = useCompanyWithPrices(open && company ? company.id : null);
    const setPrice = useSetCompanyFoodPrice();
    const deletePrice = useDeleteCompanyFoodPrice();
    const [drafts, setDrafts] = useState({}); // foodId → string price (empty = no override)
    const [filter, setFilter] = useState('');
    // overrides[foodId] = current saved override price (number) or undefined
    const overrides = useMemo(() => {
        const map = {};
        for (const p of companyQ.data?.foodPrices ?? [])
            map[p.foodId] = p.price;
        return map;
    }, [companyQ.data]);
    // Hydrate drafts from server data each time the modal opens / company changes.
    useEffect(() => {
        if (!open)
            return;
        const init = {};
        for (const [fid, price] of Object.entries(overrides))
            init[Number(fid)] = String(price);
        setDrafts(init);
        setFilter('');
    }, [open, overrides]);
    const foods = useMemo(() => {
        const list = foodsQ.data?.data ?? [];
        const q = filter.trim();
        return q ? list.filter((f) => f.name.includes(q)) : list;
    }, [foodsQ.data, filter]);
    const onSaveRow = async (foodId) => {
        if (!company)
            return;
        const raw = (drafts[foodId] ?? '').trim();
        try {
            if (raw === '') {
                // Empty → remove the override (use base price).
                if (overrides[foodId] !== undefined) {
                    await deletePrice.mutateAsync({ companyId: company.id, foodId });
                    toast.success('قیمت سفارشی حذف شد (قیمت پایه استفاده می‌شود)');
                }
                return;
            }
            const n = Number(raw.replace(/[٬\,]/g, ''));
            if (!Number.isFinite(n) || n < 0) {
                toast.error('قیمت نامعتبر');
                return;
            }
            await setPrice.mutateAsync({ companyId: company.id, foodId, price: Math.round(n) });
            toast.success('قیمت ذخیره شد');
        }
        catch (e) {
            toast.error(e?.response?.data?.error || 'خطا');
        }
    };
    const onResetRow = async (foodId) => {
        if (!company)
            return;
        if (overrides[foodId] === undefined) {
            setDrafts((d) => ({ ...d, [foodId]: '' }));
            return;
        }
        try {
            await deletePrice.mutateAsync({ companyId: company.id, foodId });
            setDrafts((d) => ({ ...d, [foodId]: '' }));
            toast.success('قیمت سفارشی حذف شد');
        }
        catch (e) {
            toast.error(e?.response?.data?.error || 'خطا');
        }
    };
    const saveAll = async () => {
        if (!company)
            return;
        let saved = 0, removed = 0, failed = 0;
        for (const f of foodsQ.data?.data ?? []) {
            const raw = (drafts[f.id] ?? '').trim();
            const currentOverride = overrides[f.id];
            try {
                if (raw === '') {
                    if (currentOverride !== undefined) {
                        await deletePrice.mutateAsync({ companyId: company.id, foodId: f.id });
                        removed++;
                    }
                }
                else {
                    const n = Number(raw.replace(/[٬\,]/g, ''));
                    if (!Number.isFinite(n) || n < 0) {
                        failed++;
                        continue;
                    }
                    if (currentOverride === Math.round(n))
                        continue;
                    await setPrice.mutateAsync({ companyId: company.id, foodId: f.id, price: Math.round(n) });
                    saved++;
                }
            }
            catch {
                failed++;
            }
        }
        toast.success(`ذخیره شد — اعمال: ${saved}، حذف: ${removed}${failed ? `، خطا: ${failed}` : ''}`);
    };
    if (!company)
        return null;
    const loading = foodsQ.isLoading || companyQ.isLoading;
    return (_jsx(Modal, { open: open, onClose: onClose, size: "xl", title: _jsxs("span", { className: "flex items-center gap-2", children: ["\u0642\u06CC\u0645\u062A\u200C\u0647\u0627\u06CC \u0627\u062E\u062A\u0635\u0627\u0635\u06CC \u0634\u0631\u06A9\u062A", _jsx("span", { className: "rounded-full bg-arshe-100 px-2 py-0.5 text-xs font-semibold text-arshe-700 dark:bg-arshe-950 dark:text-arshe-200", children: company.name })] }), footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: t('common.close') }), _jsxs(Button, { onClick: saveAll, loading: setPrice.isPending || deletePrice.isPending, children: [_jsx(CheckIcon, { className: "h-4 w-4" }), " \u0630\u062E\u06CC\u0631\u0647 \u0647\u0645\u0647"] })] }), children: loading ? _jsx(Spinner, {}) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mb-3 flex items-center justify-between gap-2", children: [_jsxs("div", { className: "relative w-full max-w-xs", children: [_jsx(MagnifyingGlassIcon, { className: "pointer-events-none absolute end-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" }), _jsx(Input, { placeholder: "\u062C\u0633\u062A\u062C\u0648\u06CC \u0646\u0627\u0645 \u063A\u0630\u0627...", value: filter, onChange: (e) => setFilter(e.target.value), className: "pe-8" })] }), _jsx("p", { className: "text-[11px] text-gray-500", children: "\u0628\u0631\u0627\u06CC \u0627\u0633\u062A\u0641\u0627\u062F\u0647 \u0627\u0632 \u0642\u06CC\u0645\u062A \u067E\u0627\u06CC\u0647\u060C \u0641\u06CC\u0644\u062F \u0631\u0627 \u062E\u0627\u0644\u06CC \u0628\u06AF\u0630\u0627\u0631\u06CC\u062F." })] }), _jsx("div", { className: "overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800", children: _jsxs("table", { className: "w-full text-right text-sm", children: [_jsx("thead", { className: "bg-gray-50 text-xs font-semibold text-gray-600 dark:bg-gray-800/60 dark:text-gray-300", children: _jsxs("tr", { children: [_jsx("th", { className: "px-3 py-2", children: "\u063A\u0630\u0627" }), _jsx("th", { className: "px-3 py-2", children: "\u0642\u06CC\u0645\u062A \u067E\u0627\u06CC\u0647" }), _jsx("th", { className: "px-3 py-2", children: "\u0642\u06CC\u0645\u062A \u0627\u062E\u062A\u0635\u0627\u0635\u06CC" }), _jsx("th", { className: "px-3 py-2", children: "\u0639\u0645\u0644\u06CC\u0627\u062A" })] }) }), _jsx("tbody", { children: foods.map((f) => {
                                    const draft = drafts[f.id] ?? '';
                                    const saved = overrides[f.id];
                                    const hasOverride = saved !== undefined;
                                    return (_jsxs("tr", { className: "border-t border-gray-100 dark:border-gray-800", children: [_jsx("td", { className: "px-3 py-2", children: _jsx("div", { className: "font-medium", children: f.name }) }), _jsx("td", { className: "px-3 py-2 text-gray-500", children: formatToman(f.basePrice) }), _jsx("td", { className: "px-3 py-2", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Input, { type: "text", dir: "ltr", placeholder: String(f.basePrice), value: draft, onChange: (e) => setDrafts((d) => ({ ...d, [f.id]: e.target.value })), onBlur: () => onSaveRow(f.id), className: "!w-36" }), draft && Number.isFinite(Number(draft)) && (_jsx("span", { className: "text-[11px] text-gray-500", children: toPersianDigits(Number(draft).toLocaleString('en-US')) }))] }) }), _jsx("td", { className: "px-3 py-2", children: hasOverride && (_jsxs("button", { onClick: () => onResetRow(f.id), title: "\u062D\u0630\u0641 \u0642\u06CC\u0645\u062A \u0627\u062E\u062A\u0635\u0627\u0635\u06CC (\u0628\u0627\u0632\u06AF\u0634\u062A \u0628\u0647 \u0642\u06CC\u0645\u062A \u067E\u0627\u06CC\u0647)", className: "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800", children: [_jsx(ArrowUturnLeftIcon, { className: "h-3.5 w-3.5" }), "\u0628\u0627\u0632\u0646\u0634\u0627\u0646\u06CC"] })) })] }, f.id));
                                }) })] }) })] })) }));
}
